/* eslint-disable no-console */
import { useEffect, useRef, useState } from "react";
import { generateHashKey } from "../utils/generateHashKey";
import { MAX_RETRIES, STREAM_SSE_EVENTS_API } from "../SSE.constants";
import { USER_LOGIN_CUSTOM_EVENT } from "features/Common/constants/customEvent.constants";
import { getSessionStorageItem } from "features/Common/modules/Storage/modules/SessionStorage/utils/SessionStorage.utils";
import { SESSION_STORAGE_KEYS } from "features/Common/modules/Storage/modules/SessionStorage/constants/SessionStorage.constant";
import { useMessageEventHandlers } from "./useMessageEventHandlers";
import { CREATOR_ROLE_POWER } from "features/Common/constants/rolePowers.constants";
import { checkUserData } from "utils/AuthUtil";

export const useSSE = () => {
  const isUserLoggedIn = checkUserData();
  const eventSourceRef = useRef(null); // Holds the SSE connection instance
  const inactiveTimerRef = useRef(null); // Tracks tab inactivity timeout
  const retryCountRef = useRef(0); // Tracks the number of retry attempts
  const [isTabActive, setIsTabActive] = useState(!document.hidden); // Tracks whether the tab is active
  const [userLoggedIn, setUserLoggedIn] = useState(isUserLoggedIn); // Tracks user login status

  useEffect(() => {
    setUserLoggedIn(isUserLoggedIn);
  }, [isUserLoggedIn]);

  const { handleMessage } = useMessageEventHandlers();

  /**
   * Establishes a Server-Sent Events (SSE) connection.
   * Ensures only one active connection at a time.
   * Retries connection up to MAX_RETRIES on failure.
   */
  const connectSSE = async () => {
    const rolePowers = JSON.parse(
      getSessionStorageItem(SESSION_STORAGE_KEYS.ROLE_POWERS) || "{}"
    );
    const isCreator = rolePowers?.role_type === CREATOR_ROLE_POWER;

    // if REACT_APP_ENABLE_SSE_FOR_ALL is true, then connect for all staff members as well else only for creators
    if (!isCreator && process.env.REACT_APP_ENABLE_SSE_FOR_ALL !== "true") {
      return;
    }

    if (eventSourceRef.current) return; // Prevent duplicate connections

    const hashKey = await generateHashKey();
    if (!hashKey) return;

    const eventSourceUrl = `${process.env.REACT_APP_SSE_API_BASE_URL}/${STREAM_SSE_EVENTS_API}?hash_key=${hashKey}`;
    eventSourceRef.current = new EventSource(eventSourceUrl);

    eventSourceRef.current.onopen = () => {
      console.log("[SSE] Connection Established! 🟢");
      retryCountRef.current = 0; // Reset retry count on successful connection
    };

    eventSourceRef.current.onerror = async () => {
      cleanupConnection("connection error");

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        console.log(
          `[SSE] Retrying... (${retryCountRef.current}/${MAX_RETRIES})`
        );
        setTimeout(() => connectSSE(), 1000); // Retry after 1 second delay
      } else {
        console.log("[SSE] Max retries reached. Stopping SSE reconnection.");
      }
    };

    eventSourceRef.current.onmessage = (event) => handleMessage(event);
  };

  /**
   * Closes the SSE connection and cleans up timers.
   * @param {string} reason - Reason for closing the connection.
   */
  const cleanupConnection = (reason = "unmount") => {
    if (eventSourceRef.current) {
      console.log("[SSE] Closing connection... Reason: ", reason);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear the inactivity timer if set
    if (inactiveTimerRef.current) {
      clearTimeout(inactiveTimerRef.current);
      inactiveTimerRef.current = null;
    }
  };

  /**
   * Handles tab visibility changes to disconnect/reconnect SSE based on inactivity.
   */
  const handleVisibilityChange = () => {
    const isHidden = document.hidden;

    if (isHidden) {
      // Start a timer to disconnect after 3 minutes of inactivity
      inactiveTimerRef.current = setTimeout(() => {
        cleanupConnection("Tab inactive");
        setIsTabActive(false);
      }, 3 * 60 * 1000); // 3 minutes
    } else {
      // Clear the inactivity timer if user returns before 3 minutes
      if (inactiveTimerRef.current) {
        clearTimeout(inactiveTimerRef.current);
        inactiveTimerRef.current = null;
      }

      if (!isTabActive) setIsTabActive(true); // Only update if status was inactive
    }
  };

  /**
   * Handles user login by setting the `userLoggedIn` state.
   */
  const handleLogin = () => {
    if (!userLoggedIn) setUserLoggedIn(true);
  };

  /**
   * useEffect to manage SSE connection lifecycle.
   * - Connects SSE when tab is active and user is logged in.
   * - Listens for tab visibility changes.
   * - Listens for custom user login events.
   */
  useEffect(() => {
    if (isTabActive && userLoggedIn) {
      connectSSE();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(USER_LOGIN_CUSTOM_EVENT, handleLogin);

    return () => {
      cleanupConnection(); // Cleanup on unmount
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(USER_LOGIN_CUSTOM_EVENT, handleLogin);
    };
  }, [isTabActive, userLoggedIn]);
};
